import React, { useContext, useEffect, useRef, useState } from 'react';
import { postSignInBySocial } from '../../../common/api/apiClient';
import { AppContext } from '../../../App';
import { doSyncRedirect, getFallbackRedirect } from '../../../common/utils/utils';
import { useTrack } from '@hackler/react-sdk';
import { setLastLoginType } from '../../../common/utils/storage';
import { LoginButton } from './LoginButton';
import { Social } from '../../../common/providers_services/LoginProvider';

type NaverLoginOptions = {
  clientId: string;
  callbackUrl: string;
  callbackHandle: boolean;
  isPopup?: boolean;
  loginButton?: {
    color?: 'green' | 'white';
    type?: 1 | 2 | 3;
    height?: number;
    width?: number;
    tabIndex?: number;
  };
};

interface NaverLogin {
  init(): void;

  getLoginStatus(cb: (status: boolean) => void): void;

  successCallback?: (data: unknown) => void;
}

export interface NaverSDK {
  LoginWithNaverId: new (opts: NaverLoginOptions) => NaverLogin;
}

type NaverMessage = {
  callback?: string;
  accessToken?: string;
};

interface Props {
  onSocial: (social: Social) => void;
  onToken: () => void;
}

export const Naver: React.FC<Props> = ({ onSocial, onToken }) => {
  const { redirect } = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const naverLogin = useRef<NaverLogin | null>(null);
  const loaded = useRef(false);
  const track = useTrack();

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    loadScript();
  }, []);

  useEffect(() => {
    if (!ready) return;
    checkLoginStatus();
  }, [ready]);

  useEffect(() => {
    const handler = (e: MessageEvent<NaverMessage>) => {
      if (e.data?.callback === 'naver' && e.data.accessToken) {
        if ((redirect ?? '').includes('sync')) {
          doSyncRedirect(redirect!, e.data.accessToken);
          return;
        }
        void handleNaverSignIn(e.data.accessToken);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [redirect]);

  const handleNaverSignIn = async (access_token: string) => {
    const res = await postSignInBySocial(redirect ?? getFallbackRedirect(), 'naver', access_token);

    if (res) {
      track({ key: 'click_login_naver' });
      // Social 타입 정의에 따라 필요하면 이 부분의 캐스팅을 조정하세요.
      onSocial({ provider: 'naver', access_token, ...res } as unknown as Social);
    } else {
      setLastLoginType('naver');
      track({ key: 'click_signup_naver' });
      onToken();
    }
  };

  const checkLoginStatus = () => {
    naverLogin.current?.getLoginStatus(() => {
      // 필요 시 status 활용
    });
  };

  const loadScript = () => {
    if (document.getElementById('naver-login-script')) {
      console.error('Naver script already loaded');
      initNaver();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://static.nid.naver.com/js/naveridlogin_js_sdk_2.0.2.js';
    script.charset = 'UTF-8';
    script.id = 'naver-login-script';
    document.head.append(script);

    script.onload = () => initNaver();
  };

  const initNaver = () => {
    if (!window.naver?.LoginWithNaverId) {
      console.error('Naver SDK not found on window');
      return;
    }

    naverLogin.current = new window.naver.LoginWithNaverId({
      clientId: 'Bf0Y2ciBsz1cz7kLu4jY',
      callbackUrl: `${window.location.origin}/social-login/naver/callback`,
      callbackHandle: true,
      isPopup: true,
      loginButton: {
        color: 'green',
        type: 1,
        height: 62,
        tabIndex: -1,
      },
    });

    naverLogin.current.successCallback = data => {
      // console.log(data);
    };

    naverLogin.current.init();
    setReady(true);
  };

  const onClick = () => {
    const n = document.getElementById('naverIdLogin_loginButton') as HTMLAnchorElement | null;
    n?.click();
  };

  return <LoginButton id={'naverIdLogin'} provider={'naver'} onClick={onClick} />;
};
